/* KarmaBurst — global floating toast that animates "+N karma" upward when triggered.
 *
 * Usage:
 *   - Mount once at app root (e.g., inside AppShell or App.js)
 *   - Other components call: window.dispatchEvent(new CustomEvent('orio:karma', { detail: { delta: 3, reason: 'hazard' } }))
 *   - The toast appears top-right, animates up + fades out
 */
import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles } from "lucide-react";

let _seq = 0;

export default function KarmaBurst() {
  const [items, setItems] = useState([]);

  const dismiss = useCallback((id) => {
    setItems((arr) => arr.filter((it) => it.id !== id));
  }, []);

  useEffect(() => {
    const onKarma = (e) => {
      const delta = Number(e?.detail?.delta) || 0;
      if (delta === 0) return;
      const id = ++_seq;
      const reason = e?.detail?.reason || "";
      setItems((arr) => [...arr, { id, delta, reason }].slice(-3));
      // auto-dismiss after 2.4s
      setTimeout(() => dismiss(id), 2400);
    };
    window.addEventListener("orio:karma", onKarma);
    return () => window.removeEventListener("orio:karma", onKarma);
  }, [dismiss]);

  return (
    <div
      data-testid="karma-burst-stack"
      className="fixed top-20 right-4 z-[60] flex flex-col items-end gap-2 pointer-events-none"
      aria-live="polite"
      aria-atomic="true"
    >
      <AnimatePresence>
        {items.map((it) => (
          <motion.div
            key={it.id}
            initial={{ x: 60, opacity: 0, scale: 0.85 }}
            animate={{ x: 0, opacity: 1, scale: 1 }}
            exit={{ y: -40, opacity: 0, scale: 0.85 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="px-3 py-1.5 rounded-full bg-white border-2 shadow-[0_10px_30px_-8px_rgba(255,122,26,0.45)] flex items-center gap-1.5"
            style={{
              borderColor: "#FF7A1A55",
              background:
                "linear-gradient(135deg, #FFFFFF 0%, #FFF4E6 100%)",
            }}
          >
            <motion.div
              animate={{ rotate: [0, 12, -12, 0] }}
              transition={{ duration: 0.8, repeat: 1 }}
            >
              <Sparkles className="h-3.5 w-3.5 text-[#FF7A1A]" />
            </motion.div>
            <span className="font-display text-[14px] font-semibold text-[#FF7A1A] tabular-nums">
              +{it.delta} karma
            </span>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

/** Helper for other components to dispatch a karma burst */
export function emitKarmaBurst(delta, reason = "") {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("orio:karma", { detail: { delta, reason } }));
}
